import useErrorHandling, { ErrorTypes } from "../../../errors/useErrorHandling";
import usePreferences from "./usePreferences";
import useSWR from "swr";
import integrationsClient from "../clients/integrationsClient";
import { setItscApprovalsTaskCount } from "../../../redux/actions/taskCountActions";
import { useDispatch } from "react-redux";
import serviceNowMapper from "../mappers/serviceNowMapper";
import useServiceNowUser from "./useServiceNowUser";
import { SERVICENOW } from "../../../constants/serviceNames";
import { useCallback, useMemo } from "react";

const useServiceNowApprovals = () => {
    const dispatch = useDispatch();
    const {
        servicesInMaintenanceMode,
        servicesBasedOnPermissions,
        preferencesAreLoading,
    } = usePreferences();
    const { serviceNowUser, serviceNowUserIsLoading } = useServiceNowUser();
    const { throwAsyncError } = useErrorHandling();

    const {
        data: serviceNowApprovals,
        isLoading: serviceNowApprovalsDataIsLoading,
    } = useSWR(
        () =>
            preferencesAreLoading || serviceNowUserIsLoading
                ? null
                : "ServiceNowApprovals",
        async () => {
            try {
                if (
                    servicesBasedOnPermissions.includes(SERVICENOW) &&
                    servicesInMaintenanceMode &&
                    serviceNowUser
                ) {
                    if (
                        !servicesInMaintenanceMode.find(
                            (service) => service === SERVICENOW
                        )
                    ) {
                        const serviceNowApprovalsData =
                            await integrationsClient.getApprovals({
                                "sn-user-data-token": serviceNowUser,
                            });
                        const approvalsMapped =
                            serviceNowMapper.mapAPIApprovalsToApprovals(
                                serviceNowApprovalsData
                            );
                        dispatch(
                            setItscApprovalsTaskCount(approvalsMapped.length)
                        );
                        return approvalsMapped;
                    }
                }
            } catch (error) {
                // Display message from API in case of 404 (user not found in ITSC)
                let message =
                    error?.response?.data?.errorCode === 404 ||
                    error?.response?.data?.errorCode === "SF-ITG-SN-400"
                        ? error?.response?.data?.detail
                        : "Unable to fetch ITSC Approvals";
                throwAsyncError(
                    ErrorTypes.ServiceNowApprovalsError,
                    message,
                    error
                );
            }
        },
        { fallbackData: [] }
    );

    const serviceNowApprovalById = useCallback(
        (id) => {
            if (serviceNowApprovals) {
                const approval = serviceNowApprovals.find((approval) => {
                    return approval.id === id;
                });
                return approval ?? {};
            }
            return {};
        },
        [serviceNowApprovals]
    );

    const serviceNowApprovalsAreLoading = useMemo(
        () =>
            serviceNowApprovalsDataIsLoading ||
            serviceNowUserIsLoading ||
            preferencesAreLoading,
        [
            serviceNowApprovalsDataIsLoading,
            serviceNowUserIsLoading,
            preferencesAreLoading,
        ]
    );

    return {
        serviceNowApprovals,
        serviceNowApprovalsAreLoading,
        serviceNowApprovalById,
    };
};

export default useServiceNowApprovals;
